import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["tableKey"],
  _excluded2 = ["onClick", "disabled"];
var _IconHorizontalRule, _TableCellCustom, _Skeleton;
import * as React from 'react';
import _ from 'lodash';
import { IconHorizontalRule, Skeleton, TableRow, Typography, TableBody as UiCoreTableBody, TableCell as UiCoreTableCell } from '@lumahealthhq/ui-core';
import { useGrabScroll } from '@lumahealthhq/ui-utils';
import { useDataResourceContext } from '../../contexts/DataResourceContext';
import { filterObject } from '../../utils';
import CheckboxCell from '../CheckboxCell';
import TableCellCustom from '../TableCellCustom';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function TableCell({
  value,
  isFirstElement,
  draggedRef
}) {
  const {
    itemToRender,
    tableCellProps,
    hasCustomRender,
    isMenu
  } = value;
  const renderWithPlaceholder = itemToRender != null ? itemToRender : _IconHorizontalRule || (_IconHorizontalRule = /*#__PURE__*/_jsx(IconHorizontalRule, {}));
  const handleClick = e => {
    if (!draggedRef.current) {
      var _tableCellProps$onCli;
      tableCellProps == null ? void 0 : (_tableCellProps$onCli = tableCellProps.onClick) == null ? void 0 : _tableCellProps$onCli.call(tableCellProps, e);
    }
  };
  return /*#__PURE__*/_jsx(TableCellCustom, _extends({
    isMenu: isMenu,
    isFirstElement: isFirstElement
  }, tableCellProps, {
    onClick: handleClick,
    children: hasCustomRender ? renderWithPlaceholder : /*#__PURE__*/_jsx(Typography, {
      variant: "body1",
      noWrap: true,
      children: renderWithPlaceholder
    })
  }));
}
const MemoizedTableCell = /*#__PURE__*/React.memo(TableCell);
function Row({
  item,
  firstRow,
  itemToCheckbox,
  withCheckbox,
  draggedRef,
  customProps,
  onRowClick
}) {
  const {
      tableKey
    } = item,
    restItem = _objectWithoutPropertiesLoose(item, _excluded);
  const cellsToRender = React.useMemo(() => Object.values(restItem), [restItem]);
  const _ref = customProps != null ? customProps : {
      onClick: null
    },
    {
      onClick: customClick,
      disabled: disableRow = () => false
    } = _ref,
    restCustomProps = _objectWithoutPropertiesLoose(_ref, _excluded2);
  const isRowDisabled = React.useMemo(() => disableRow(itemToCheckbox), [disableRow, itemToCheckbox]);
  const renderedCells = React.useMemo(() => cellsToRender.map((value, idx) => {
    const isFirstElement = withCheckbox ? false : idx === 0;
    return /*#__PURE__*/_jsx(MemoizedTableCell, {
      value: value,
      draggedRef: draggedRef,
      isFirstElement: isFirstElement
    }, `${tableKey}-${idx}-table-cell`);
  }), [cellsToRender, withCheckbox, tableKey, draggedRef]);
  const shouldRenderCheckbox = React.useMemo(() => {
    const isFunction = withCheckbox instanceof Function;
    if (!isFunction) {
      return true;
    }
    return !!(withCheckbox != null && withCheckbox(itemToCheckbox));
  }, [itemToCheckbox, withCheckbox]);
  const handleRowClick = React.useCallback(e => {
    if (customClick && !draggedRef.current && !isRowDisabled) {
      customClick(e, itemToCheckbox);
    }
    onRowClick == null ? void 0 : onRowClick(e, itemToCheckbox);
  }, [customClick, draggedRef, itemToCheckbox, isRowDisabled, onRowClick]);
  const applyHoverEffect = (!!customClick || !!onRowClick) && !isRowDisabled;
  return /*#__PURE__*/_jsxs(TableRow, _extends({
    onClick: handleRowClick
  }, restCustomProps, {
    sx: theme => ({
      height: '55px',
      '& > td': {
        borderBottom: 0,
        borderTop: firstRow ? 0 : 1,
        borderColor: theme.palette.background.default
      },
      '&:active': {
        cursor: 'grabbing !important',
        '& > td': {
          bgcolor: applyHoverEffect ? `${theme.palette.primary[200]} !important` : undefined
        }
      },
      '&:hover, &:focus': {
        borderRadius: 1,
        '& > td:first-of-type': {
          borderTopLeftRadius: theme.spacing(),
          borderBottomLeftRadius: theme.spacing()
        },
        '& > td:last-of-type': {
          borderTopRightRadius: theme.spacing(),
          borderBottomRightRadius: theme.spacing()
        },
        '& > td': {
          bgcolor: applyHoverEffect ? theme.palette.background.default : undefined
        },
        '& + tr > td': {
          borderColor: applyHoverEffect ? 'white' : undefined
        }
      }
    }),
    children: [withCheckbox && /*#__PURE__*/_jsx(TableCellCustom, {
      isCheckbox: true,
      isFirstElement: true,
      children: /*#__PURE__*/_jsx(CheckboxCell, {
        disabled: !shouldRenderCheckbox,
        itemId: tableKey
      })
    }), renderedCells]
  }));
}
const MemoizedRow = /*#__PURE__*/React.memo(Row);

/**
 * @ignore - internal component.
 */
function TableBody({
  parentChildren,
  withCheckbox,
  isLoading,
  parentId,
  rowProps,
  onRowClick
}) {
  const {
    data
  } = useDataResourceContext();
  const draggedRef = useGrabScroll(parentId);
  const filteredDataWithRenderProp = React.useMemo(() => {
    var _data$map;
    return (_data$map = data.map(item => filterObject(item, parentChildren))) != null ? _data$map : [];
  }, [data, parentChildren]);
  const columnsLenght = _.get(parentChildren, 'length');
  return /*#__PURE__*/_jsxs(UiCoreTableBody, {
    sx: {
      position: 'relative'
    },
    children: [isLoading && _.times(3, row => /*#__PURE__*/_jsxs(TableRow, {
      sx: {
        pl: 2,
        height: 55
      },
      children: [withCheckbox ? _TableCellCustom || (_TableCellCustom = /*#__PURE__*/_jsx(TableCellCustom, {
        isCheckbox: true,
        children: /*#__PURE__*/_jsx(CheckboxCell, {
          disabled: true
        })
      })) : null, _.times(columnsLenght, col => /*#__PURE__*/_jsx(UiCoreTableCell, {
        children: _Skeleton || (_Skeleton = /*#__PURE__*/_jsx(Skeleton, {
          width: '100%',
          height: '32px'
        }))
      }, col))]
    }, row)), !isLoading && filteredDataWithRenderProp.map((item, index) => {
      return /*#__PURE__*/_jsx(MemoizedRow, {
        draggedRef: draggedRef,
        item: item,
        firstRow: index === 0,
        itemToCheckbox: data[index],
        withCheckbox: withCheckbox,
        customProps: rowProps,
        onRowClick: onRowClick
      }, `${item.tableKey}-table-row`);
    })]
  });
}
export default TableBody;